/**
 * Agent-friendly historian wrapper for the gateway AI. Mirrors the
 * editor's historianQuery.js: path-to-id cache + wire-shape projection
 * into compact {t, v, ...} rows so a 500-bucket response doesn't burn
 * tokens on field names the model doesn't need.
 */

import * as api from "../../api";

// ── path→id cache ──────────────────────────────────────────────────
const PATH_TTL_MS = 5 * 60 * 1000;
const _pathCache = new Map();

export function invalidatePathCache() {
  _pathCache.clear();
}

async function resolvePath(tagPath) {
  if (typeof tagPath !== "string" || !tagPath.trim()) {
    throw new Error("historianBackend: tag_path is required");
  }
  const key = tagPath.trim();
  const cached = _pathCache.get(key);
  if (cached && Date.now() - cached.at < PATH_TTL_MS) return cached.id;
  const id = await api.resolveTagPathToId(key);
  if (id == null) {
    throw new Error(
      `historianBackend: tag "${tagPath}" not found. Use search_tags to discover valid paths.`,
    );
  }
  _pathCache.set(key, { id, at: Date.now() });
  return id;
}

// ── wire-shape projection ──────────────────────────────────────────
// Raw rows arrive with `value_type` + a set of value_* columns. Always
// key off value_type — ClickHouse fills unused numeric columns with 0,
// so a "first non-null" fallback would silently return false for every
// Float64 tag.
function qualityLabel(code) {
  const n = Number(code);
  if (!Number.isInteger(n)) return null;
  if (n === 0) return "bad";
  if (n === 64) return "uncertain";
  if (n === 192) return "good";
  return null;
}

function projectRawRow(row) {
  if (!row || typeof row !== "object") return null;
  const t = row.source_time || row.recv_time || null;
  const typeCode = Number(row.value_type);
  let v = null;
  switch (typeCode) {
    case 0: v = Boolean(row.value_bool); break;
    case 1: v = Number(row.value_int); break;
    case 2: v = Number(row.value_num); break;
    case 3: v = typeof row.value_str === "string" ? row.value_str : ""; break;
    case 5: {
      const raw = row.value_json;
      if (raw == null) v = null;
      else if (typeof raw === "object") v = raw;
      else if (typeof raw === "string") {
        const trimmed = raw.trim();
        if (!trimmed) { v = null; break; }
        try { v = JSON.parse(trimmed); } catch { v = raw; }
      }
      break;
    }
    default: v = null;
  }
  const qualityRaw = row.quality ?? null;
  return {
    t,
    v,
    quality: qualityRaw,
    quality_label: qualityRaw != null ? qualityLabel(qualityRaw) : null,
  };
}

function projectAggBucket(b) {
  if (!b || typeof b !== "object") return null;
  return {
    t: b.bucket_start,
    min: b.min != null ? Number(b.min) : null,
    max: b.max != null ? Number(b.max) : null,
    avg: b.avg != null ? Number(b.avg) : null,
    last: b.last != null ? Number(b.last) : null,
    count: b.count != null ? Number(b.count) : 0,
  };
}

// ── public wrappers (called by tools) ──────────────────────────────

export async function queryAggregated(tagPath, { start, end, interval }) {
  const tagId = await resolvePath(tagPath);
  const rows = await api.fetchHistoryAggregate({ tagId, start, end, interval });
  const points = (Array.isArray(rows) ? rows : []).map(projectAggBucket).filter(Boolean);
  return { tagId, tagPath, points };
}

export async function queryRaw(tagPath, { start, end, limit = 10000, order = "asc" }) {
  const tagId = await resolvePath(tagPath);
  const rows = await api.fetchHistoryRange({ tagId, start, end, limit, order });
  const points = (Array.isArray(rows) ? rows : []).map(projectRawRow).filter(Boolean);
  return { tagId, tagPath, points };
}

export async function queryLatest(tagPath, n = 1) {
  const tagId = await resolvePath(tagPath);
  const rows = await api.fetchHistoryLast({ tagId, n });
  const points = (Array.isArray(rows) ? rows : []).map(projectRawRow).filter(Boolean);
  return { tagId, tagPath, points };
}

export async function currentValue(tagPath) {
  // Gateway has no dedicated "current value" endpoint — fetch the
  // most-recent historian sample, which is effectively the same thing
  // under SCADA's zero-order-hold semantics.
  const result = await queryLatest(tagPath, 1);
  const point = result.points[0];
  if (!point) return null;
  return { tagId: result.tagId, tagPath, ...point };
}

export async function tagMetadata(tagPath) {
  const tagId = await resolvePath(tagPath);
  const tag = await api.fetchTagById(tagId);
  if (!tag) return null;
  return { tagId, tagPath, ...tag };
}

export async function findAnomalies(tagPath, opts = {}) {
  const tagId = await resolvePath(tagPath);
  const rows = await api.fetchHistoryAnomalies({
    tagId,
    start: opts.start,
    end: opts.end,
    method: opts.method,
    threshold: opts.threshold,
    window: opts.window,
    percentile: opts.percentile,
    clusterGapSeconds: opts.cluster_gap_seconds ?? opts.clusterGapSeconds ?? 60,
  });
  return { tagId, tagPath, rows: Array.isArray(rows) ? rows : [] };
}

export async function distribution(tagPath, { start, end, bins, percentiles, approximate } = {}) {
  const tagId = await resolvePath(tagPath);
  const pctList = Array.isArray(percentiles) && percentiles.length ? percentiles.join(",") : undefined;
  const result = await api.fetchDistribution({
    tagId, start, end, bins,
    percentiles: pctList,
    approximate,
  });
  return { tagId, tagPath, result: result || null };
}

export async function changePoints(tagPath, { start, end, threshold, window } = {}) {
  const tagId = await resolvePath(tagPath);
  const rows = await api.fetchChangePoints({ tagId, start, end, threshold, window });
  return { tagId, tagPath, rows: Array.isArray(rows) ? rows : [] };
}

export async function rateOfChange(tagPath, { start, end, unit } = {}) {
  const tagId = await resolvePath(tagPath);
  const result = await api.fetchRateOfChange({ tagId, start, end, unit });
  return { tagId, tagPath, result: result || null };
}

export async function analyzeTimeseries(tagPath, { start, end } = {}) {
  const tagId = await resolvePath(tagPath);
  const result = await api.fetchTimeseriesStats({ tagId, start, end });
  return { tagId, tagPath, result: result || null };
}

const DEFAULT_VALUE_AT_LOOKBACK_MS = 7 * 24 * 60 * 60 * 1000;

export async function valueAt(tagPath, targetTimestamp, { lookbackMs = DEFAULT_VALUE_AT_LOOKBACK_MS } = {}) {
  const tagId = await resolvePath(tagPath);
  const targetMs = new Date(targetTimestamp).getTime();
  if (!Number.isFinite(targetMs)) {
    throw new Error(`historianBackend.valueAt: invalid timestamp "${targetTimestamp}"`);
  }
  const startIso = new Date(targetMs - lookbackMs).toISOString();
  const endIso = new Date(targetMs).toISOString();
  const rows = await api.fetchHistoryRange({ tagId, start: startIso, end: endIso, limit: 1, order: "desc" });
  const row = Array.isArray(rows) && rows.length ? rows[0] : null;
  if (!row) return null;
  const projected = projectRawRow(row);
  if (!projected) return null;
  const sampleMs = projected.t ? new Date(projected.t).getTime() : NaN;
  const ageMs = Number.isFinite(sampleMs) ? targetMs - sampleMs : null;
  return { tagId, tagPath, ...projected, ageMs };
}
